import React, { useState, useEffect } from "react";
import {
  Search,
  Keyboard,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  User,
} from "lucide-react";
import { getTypes, postQuestion, QuestionTrue } from "../../services/api";
import { useLanguage } from "../../contexts/LanguageContext";

import LibraryFaq from "../HomeComponents/LibraryFaq";

const FAQ_CATEGORY = {
  id: "library-faq",
  titles: {
    kz: "Жиі қойылатын сұрақтар",
    ru: "Часто задаваемые вопросы",
    en: "Frequently Asked Questions",
  },
};

const translations = {
  kz: {
    askQuestion: "Сұрақ қою",
    allCategories: "Барлығы",
    searchPlaceholder: "Кілт сөздер арқылы іздеу",
    noResults: "Сұрақтар табылмады",
    loading: "Жүктелуде...",
    questionDate: "Дата жоқ",
    questionAuthor: "Автор",
    showAnswer: "Жауапты көрсету",
    hideAnswer: "Жауапты жасыру",
    inputName: "Аты",
    inputLastName: "Тегі",
    inputQuestion: "Сұрағыңыз",
    submit: "Жіберу",
    questionSuccess: "Сұрақ сәтті жіберілді!",
    questionError: "Сұрақты жіберу кезінде қате болды.",
    categories: "Категориялар",
    questions: "Сұрақтар",
  },
  ru: {
    askQuestion: "Задать вопрос",
    allCategories: "Все категории",
    searchPlaceholder: "Поиск по ключевым словам",
    noResults: "Вопросы не найдены",
    loading: "Загрузка...",
    questionDate: "Дата отсутствует",
    questionAuthor: "Автор",
    showAnswer: "Показать ответ",
    hideAnswer: "Скрыть ответ",
    inputName: "Имя",
    inputLastName: "Фамилия",
    inputQuestion: "Ваш вопрос",
    submit: "Отправить",
    questionSuccess: "Вопрос успешно отправлен!",
    questionError: "Ошибка при отправке вопроса.",
    categories: "Категории",
    questions: "Вопросы",
  },
  en: {
    askQuestion: "Ask a Question",
    allCategories: "All Categories",
    searchPlaceholder: "Search by keywords",
    noResults: "No questions found",
    loading: "Loading...",
    questionDate: "Date not available",
    questionAuthor: "Author",
    showAnswer: "Show Answer",
    hideAnswer: "Hide Answer",
    inputName: "Name",
    inputLastName: "Last Name",
    inputQuestion: "Your question",
    submit: "Submit",
    questionSuccess: "Question submitted successfully!",
    questionError: "Error while submitting question.",
    categories: "Categories",
    questions: "Questions",
  },
};

const Sidebar = ({
  categories,
  selectedCategory,
  onCategorySelect,
  onAskQuestion,
  isOpen,
  onClose,
}) => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage] || translations.kz;

  return (
    <>
      {/* Desktop Sidebar */}
      <div className={`hidden lg:flex lg:flex-col lg:w-80 bg-white border-r border-gray-200 min-h-screen`}>
        <div className="p-6 border-b border-gray-200">
          <button 
            className="w-full text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 shadow-sm hover:opacity-90"
            onClick={onAskQuestion}
            style={{ backgroundColor: 'var(--primary-color)' }}
          >
            {t.askQuestion}
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            <div
              className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                selectedCategory === null 
                  ? "text-white border" 
                  : "hover:bg-gray-50 text-gray-700"
              }`}
              onClick={() => onCategorySelect(null)}
              style={selectedCategory === null ? { 
                backgroundColor: 'var(--primary-color)', 
                borderColor: 'var(--primary-color)' 
              } : {}}
            >
              {t.allCategories}
            </div>
            {categories.map((category) => (
              <div
                key={category.id}
                className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                  selectedCategory === category.id
                    ? "text-white border"
                    : "hover:bg-gray-50 text-gray-700"
                }`}
                onClick={() => onCategorySelect(category.id)}
                style={selectedCategory === category.id ? { 
                  backgroundColor: 'var(--primary-color)', 
                  borderColor: 'var(--primary-color)' 
                } : {}}
              >
                {category.titles[currentLanguage]}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div 
            className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50"
            onClick={onClose}
            style={{
              animation: 'fadeInOverlay 0.3s ease forwards'
            }}
          />
          
          {/* Sidebar */}
          <div 
            className={`lg:hidden fixed top-0 left-0 w-85% max-w-320px h-full bg-white shadow-xl z-1001 transform transition-transform duration-400 ease-out ${
              isOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
            style={{
              background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fc 100%)',
              boxShadow: '2px 0 15px rgba(0, 0, 0, 0.1)',
              width: '85%',
              maxWidth: '320px',
              zIndex: 1001,
            }}
          >
            {/* Content с прокруткой */}
            <div 
              className="h-full overflow-y-auto overflow-x-hidden flex flex-col"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(222, 205, 144, 0.3) transparent'
              }}
            >
              {/* Header with close button */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
                <h2 className="text-lg font-semibold text-gray-900">{t.categories}</h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Ask Question Button */}
              <div className="p-4 border-b flex-shrink-0" style={{ borderColor: 'rgba(222, 205, 144, 0.1)' }}>
                <button 
                  className="w-full text-white px-4 py-3 rounded-lg font-medium transition-all duration-300 hover:opacity-90 flex items-center justify-center gap-2"
                  onClick={() => {
                    onAskQuestion();
                    onClose();
                  }}
                  style={{ 
                    backgroundColor: 'var(--primary-color)',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <User className="w-4 h-4" />
                  {t.askQuestion}
                </button>
              </div>
              
              {/* Categories */}
              <div className="p-4 flex-1">
                <div className="space-y-2">
                  <div
                    className={`p-4 rounded-lg cursor-pointer transition-all duration-300 border-l-4 ${
                      selectedCategory === null 
                        ? "text-white border-l-white" 
                        : "hover:bg-gray-50 text-gray-700 border-l-transparent hover:border-l-gray-300"
                    }`}
                    onClick={() => {
                      onCategorySelect(null);
                      onClose();
                    }}
                    style={selectedCategory === null ? { 
                      background: 'linear-gradient(90deg, rgba(222, 205, 144, 0.1) 0%, transparent 100%)',
                      backgroundColor: 'var(--primary-color)',
                      borderLeftColor: '#decd90'
                    } : {}}
                  >
                    {t.allCategories}
                  </div>
                  {categories.map((category) => (
                    <div
                      key={category.id}
                      className={`p-4 rounded-lg cursor-pointer transition-all duration-300 border-l-4 ${
                        selectedCategory === category.id
                          ? "text-white border-l-white"
                          : "hover:bg-gray-50 text-gray-700 border-l-transparent hover:border-l-gray-300"
                      }`}
                      onClick={() => {
                        onCategorySelect(category.id);
                        onClose();
                      }}
                      style={selectedCategory === category.id ? { 
                        background: 'linear-gradient(90deg, rgba(222, 205, 144, 0.1) 0%, transparent 100%)',
                        backgroundColor: 'var(--primary-color)',
                        borderLeftColor: '#decd90'
                      } : {}}
                    >
                      {category.titles[currentLanguage]}
                    </div>
                  ))}
                </div>
              </div>

              {/* Spacer для прокрутки */}
              <div style={{ height: '20px', width: '100%' }} className="flex-shrink-0" />
            </div>
          </div>
        </>
      )}

      {/* CSS Styles */}
      <style jsx>{`
        @keyframes fadeInOverlay {
          to {
            opacity: 1;
          }
        }

        /* Стилизация полосы прокрутки для WebKit браузеров */
        .lg\\:hidden div::-webkit-scrollbar {
          width: 6px;
        }

        .lg\\:hidden div::-webkit-scrollbar-track {
          background: transparent;
        }

        .lg\\:hidden div::-webkit-scrollbar-thumb {
          background: rgba(222, 205, 144, 0.3);
          border-radius: 3px;
        }

        .lg\\:hidden div::-webkit-scrollbar-thumb:hover {
          background: rgba(222, 205, 144, 0.5);
        }

        /* Улучшения для прокрутки на устройствах с тач-скрином */
        @media (hover: none) and (pointer: coarse) {
          .lg\\:hidden div {
            -webkit-overflow-scrolling: touch;
            scroll-behavior: smooth;
          }

          .lg\\:hidden div::-webkit-scrollbar {
            width: 8px;
          }

          .lg\\:hidden div::-webkit-scrollbar-thumb {
            background: rgba(222, 205, 144, 0.4);
            border-radius: 4px;
          }
        }
      `}</style>
    </>
  );
};

const SearchBar = ({ searchQuery, setSearchQuery, onSearch }) => {
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage] || translations.kz;

  const keyboardLayout = [
    ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "Delete"],
    ["й", "ц", "у", "к", "е", "н", "г", "ш", "щ", "з", "х", "ъ"],
    ["ф", "ы", "в", "а", "п", "р", "о", "л", "д", "ж", "э"],
    ["я", "ч", "с", "м", "и", "т", "ь", "б", "ю"],
    ["ә", "і", "ң", "ғ", "ү", "ұ", "қ", "ө", "һ"],
  ];

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleKeyClick = (key) => {
    if (key === "Delete") {
      setSearchQuery((prev) => prev.slice(0, -1));
    } else {
      setSearchQuery((prev) => prev + key);
    }
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onSearch();
    }
  };

  return (
    <div className="w-full mb-6">
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={t.searchPlaceholder}
          className="w-full pl-4 pr-20 py-3 border border-gray-300 rounded-lg outline-none transition-all duration-200 text-sm md:text-base"
          style={{ 
            '--tw-ring-color': 'var(--primary-color)',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--primary-color)';
            e.target.style.boxShadow = `0 0 0 2px ${getComputedStyle(document.documentElement).getPropertyValue('--primary-color')}20`;
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#d1d5db';
            e.target.style.boxShadow = 'none';
          }}
        />
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
          {!isMobile && (
            <>
              <button
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200 keyboard-btn"
                onClick={() => setShowKeyboard(!showKeyboard)}
              >
                <Keyboard className="w-4 h-4" />
              </button>
              <div className="w-px h-4 bg-gray-300" />
            </>
          )}
          <button
            className="p-2 text-gray-400 transition-colors duration-200"
            onClick={onSearch}
            style={{ 
              '--hover-color': 'var(--primary-color)'
            }}
            onMouseEnter={(e) => {
              e.target.style.color = 'var(--primary-color)';
            }}
            onMouseLeave={(e) => {
              e.target.style.color = '#9ca3af';
            }}
          >
            <Search className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {!isMobile && showKeyboard && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          {keyboardLayout.map((row, rowIndex) => (
            <div key={rowIndex} className="flex justify-center gap-1 mb-2 last:mb-0">
              {row.map((key) => (
                <button
                  key={key}
                  className={`px-3 py-2 bg-white border border-gray-200 rounded text-sm hover:bg-gray-100 transition-colors duration-200 ${
                    key === "Delete" ? "px-4 text-red-600" : ""
                  }`}
                  onClick={() => handleKeyClick(key)}
                >
                  {key}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const QuestionItem = ({ question }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage] || translations.kz;

  const fullName = `${question.Name} ${question.LastName}`;
  const questionText = question.Question;
  const answer = question.Answer?.answer;
  const category = question.Answer?.Type?.title;

  const date = question.Answer?.createdAt
    ? new Date(question.Answer.createdAt).toISOString().split("T")[0]
    : t.questionDate;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 mb-4 overflow-hidden">
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2 sm:mb-0">
            <span className="text-sm text-gray-500">{date}</span>
            <span className="text-sm font-semibold text-gray-700">{fullName}</span>
          </div>
          {category && (
            <div 
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white self-start sm:self-center"
              style={{ backgroundColor: 'var(--primary-color)' }}
            >
              {category}
            </div>
          )}
        </div>
        
        <div className="text-gray-900 font-medium mb-4 leading-relaxed">
          {questionText}
        </div>

        {answer && (
          <div className="border-t border-gray-100 pt-4">
            <button
              className="flex items-center text-sm font-medium transition-colors duration-200"
              onClick={() => setIsExpanded(!isExpanded)}
              style={{ color: 'var(--primary-color)' }}
              onMouseEnter={(e) => {
                const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-color');
                e.target.style.filter = 'brightness(0.8)';
              }}
              onMouseLeave={(e) => {
                e.target.style.filter = 'brightness(1)';
              }}
            >
              <span className="mr-2">
                {isExpanded ? t.hideAnswer : t.showAnswer}
              </span>
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {isExpanded && (
              <div className="mt-3 p-4 bg-gray-50 rounded-lg text-gray-700 leading-relaxed">
                {answer}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AskQuestionModal = ({ isOpen, onClose, onQuestionSubmitted }) => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage] || translations.kz;

  const [form, setForm] = useState({
    Name: "",
    LastName: "",
    Question: "",
  });

  const handleSubmit = async () => {
    if (!form.Name || !form.LastName || !form.Question) {
      alert("Пожалуйста, заполните все поля");
      return;
    }

    try {
      const res = await postQuestion(form);

      if (res.newQuestion) {
        alert(t.questionSuccess);
        setForm({ Name: "", LastName: "", Question: "" });
        onClose();
        if (onQuestionSubmitted) {
          onQuestionSubmitted();
        }
      }
    } catch (error) {
      alert(t.questionError);
    }
  };

  // Блокируем прокрутку фона при открытии модала
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Очистка при размонтировании компонента
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ top: '0' }}>
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div 
        className="relative bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        style={{ 
          marginTop: '200px', // Отступ под HeaderMobile
          maxHeight: 'calc(100vh - 220px)' // Учитываем отступ сверху
        }}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">{t.askQuestion}</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <input
                type="text"
                placeholder={t.inputName}
                value={form.Name}
                onChange={(e) => setForm({ ...form, Name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition-all duration-200"
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--primary-color)';
                  e.target.style.boxShadow = `0 0 0 2px ${getComputedStyle(document.documentElement).getPropertyValue('--primary-color')}20`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            
            <div>
              <input
                type="text"
                placeholder={t.inputLastName}
                value={form.LastName}
                onChange={(e) => setForm({ ...form, LastName: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition-all duration-200"
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--primary-color)';
                  e.target.style.boxShadow = `0 0 0 2px ${getComputedStyle(document.documentElement).getPropertyValue('--primary-color')}20`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            
            <div>
              <textarea
                placeholder={t.inputQuestion}
                value={form.Question}
                onChange={(e) => setForm({ ...form, Question: e.target.value })}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition-all duration-200 resize-none"
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--primary-color)';
                  e.target.style.boxShadow = `0 0 0 2px ${getComputedStyle(document.documentElement).getPropertyValue('--primary-color')}20`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            
            <button 
              onClick={handleSubmit}
              className="w-full text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 hover:opacity-90"
              style={{ backgroundColor: 'var(--primary-color)' }}
            >
              {t.submit}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Questions() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const questionsPerPage = 10;

  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage] || translations.kz;

  // Закрытие sidebar при прокрутке
  useEffect(() => {
    const handleScroll = () => {
      if (isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isSidebarOpen]);

  // Load categories on component mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const typeResponse = await getTypes();
        if (typeResponse.types) {
          setCategories(typeResponse.types);
        }
      } catch (error) {
        console.error("Ошибка при загрузке категорий", error);
      }
    };

    loadCategories();
  }, []);

  // Function to fetch questions with filters
  const fetchQuestions = async (filters = {}) => {
    try {
      setIsLoading(true);
      const response = await QuestionTrue(filters);

      if (response && response.questions) {
        setQuestions(response.questions);
      } else {
        setQuestions([]);
      }
    } catch (error) {
      console.error("Ошибка при загрузке вопросов", error);
      setQuestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load initial questions on component mount
  useEffect(() => {
    fetchQuestions();
  }, []);

  // Handle search
  const handleSearch = () => {
    const filters = {};

    if (searchQuery.trim()) {
      filters.query = searchQuery.trim();
    }

    if (selectedCategory && selectedCategory !== FAQ_CATEGORY.id) {
      filters.typeId = selectedCategory;
    }

    setCurrentPage(1); // Reset to first page on search
    fetchQuestions(filters);
  };

  // Apply filters when category changes
  useEffect(() => {
    if (selectedCategory === FAQ_CATEGORY.id) return;
    const filters = {};

    if (searchQuery.trim()) {
      filters.query = searchQuery.trim();
    }

    if (selectedCategory && selectedCategory !== FAQ_CATEGORY.id) {
      filters.typeId = selectedCategory;
    }

    setCurrentPage(1);
    fetchQuestions(filters);
  }, [selectedCategory]);

  // Calculate pagination
  const indexOfLastQuestion = currentPage * questionsPerPage;
  const indexOfFirstQuestion = indexOfLastQuestion - questionsPerPage;
  const currentQuestions = questions.slice(
    indexOfFirstQuestion,
    indexOfLastQuestion
  );
  const totalPages = Math.ceil(questions.length / questionsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0); // Scroll to top when changing page
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex min-h-screen">
        <Sidebar
          categories={[FAQ_CATEGORY, ...categories]}
          selectedCategory={selectedCategory}
          onCategorySelect={setSelectedCategory}
          onAskQuestion={() => setIsModalOpen(true)}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile Header */}
          <div className="lg:hidden bg-white border-b border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors duration-200"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-lg font-semibold text-gray-900">{t.questions}</h1>
              <button
                onClick={() => setIsModalOpen(true)}
                className="text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 hover:opacity-90"
                style={{ backgroundColor: 'var(--primary-color)' }}
              >
                {t.askQuestion}
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
              {selectedCategory === FAQ_CATEGORY.id ? <LibraryFaq /> : (
                <>
              <SearchBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onSearch={handleSearch}
              />

              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div 
                      className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4"
                      style={{ borderBottomColor: 'var(--primary-color)' }}
                    ></div>
                    <p className="text-gray-600">{t.loading}</p>
                  </div>
                </div>
              ) : questions.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <Search className="w-12 h-12 mx-auto" />
                  </div>
                  <p className="text-gray-600 text-lg">{t.noResults}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {currentQuestions.map((question) => (
                    <QuestionItem key={question.id} question={question} />
                  ))}
                </div>
              )}

              {questions.length > 0 && totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-8 pb-4">
                  <button
                    className="p-2 rounded-md border border-gray-300 text-gray-500 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                      let pageNumber;
                      if (totalPages <= 5) {
                        pageNumber = index + 1;
                      } else if (currentPage <= 3) {
                        pageNumber = index + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNumber = totalPages - 4 + index;
                      } else {
                        pageNumber = currentPage - 2 + index;
                      }

                      return (
                        <button
                          key={pageNumber}
                          className={`px-3 py-2 rounded-md border text-sm font-medium transition-colors duration-200 ${
                            currentPage === pageNumber
                              ? "text-white"
                              : "border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                          onClick={() => handlePageChange(pageNumber)}
                          style={currentPage === pageNumber ? { 
                            backgroundColor: 'var(--primary-color)', 
                            borderColor: 'var(--primary-color)' 
                          } : {}}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    className="p-2 rounded-md border border-gray-300 text-gray-500 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <AskQuestionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onQuestionSubmitted={() => {
          fetchQuestions({
            ...(selectedCategory && selectedCategory !== FAQ_CATEGORY.id && { typeId: selectedCategory }),
            ...(searchQuery.trim() && { query: searchQuery.trim() }),
          });
        }}
      />
    </div>
  );
}